import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/esm/Add';
import ExpandMoreIcon from '@mui/icons-material/esm/ExpandMore';
import { RootState } from '../../store';
import PersonnelFolderCard from '../../components/field/PersonnelFolderCard';
import { PageHeader } from '../../components/layout';
import RoleTaskQueue from '../../components/workspaces/RoleTaskQueue';
import roleWorkspaceService, { RoleTaskSourceLink, StoresWorkspace } from '../../services/api/roleWorkspaceService';
import procurementService, { ProcurementCatalog, PurchaseOrder } from '../../services/api/procurementService';
import inventoryMovementService, { CreateInventoryMovementPayload } from '../../services/api/inventoryMovementService';
import taskMaterialsService, { TaskMaterial } from '../../services/api/taskMaterialsService';
import resourceService, { InventorySummaryRow } from '../../services/api/resourceService';
import teamService, { TeamListItem } from '../../services/api/teamService';
import YardRouteMap from '../../components/yard/YardRouteMap';
import { ACCOUNTS_PERSONNEL_QUERY_KEY, fieldPersonnelById, storesFieldPersonnel } from '../../routes/fieldPersonnel';
import { YardZoneGuidance, yardZoneGuidanceForResource, yardZoneLabel } from '../../utils/yardZoneGuidance';

type LegacyTab = 'tasks' | 'put-away' | 'movements';
type StoresFieldView = 'home' | 'receiving' | 'picking' | 'stock';

interface StoresPageProps {
  fieldMode?: boolean;
  view?: StoresFieldView;
}

type MovementType = CreateInventoryMovementPayload['movementType'];

const EMPTY_MOVEMENT = {
  movementType: 'issue' as MovementType,
  resourceId: '',
  storageLocationId: '',
  taskId: '',
  crewId: '',
  quantity: 1,
  reasonCode: '',
  notes: '',
};

const issuedPickingQuantity = (
  material: TaskMaterial,
  movements: StoresWorkspace['inventoryMovements'] = [],
  issuedAfter?: string | null
) => movements
  .filter((movement) =>
    movement.movementType === 'issue'
    && movement.referenceType === 'task_resource'
    && String(movement.referenceId ?? '') === material.id
    && (!issuedAfter || new Date(movement.createdAt || 0).getTime() >= new Date(issuedAfter).getTime())
  )
  .reduce((sum, movement) => sum + Math.abs(Number(movement.quantity || 0)), 0);

const remainingPickingQuantity = (
  material: TaskMaterial,
  movements: StoresWorkspace['inventoryMovements'] = [],
  issuedAfter?: string | null
) =>
  Math.max(0, Number(material.quantityRequired) - (
    issuedAfter
      ? issuedPickingQuantity(material, movements, issuedAfter)
      : Math.max(Number(material.quantityAllocated || 0), issuedPickingQuantity(material, movements))
  ));

const availableStockQuantity = (rows: InventorySummaryRow[] = []) =>
  rows.reduce((sum, row) => sum + Math.max(0, Number(row.quantityAvailable || 0)), 0);

const issuedPickingMovements = (
  materials: TaskMaterial[],
  movements: StoresWorkspace['inventoryMovements'] = [],
  issuedAfter?: string | null
) => {
  const materialById = new Map(materials.map((material) => [material.id, material]));
  return movements
    .filter((movement) =>
      movement.movementType === 'issue'
      && movement.referenceType === 'task_resource'
      && Boolean(movement.referenceId)
      && materialById.has(String(movement.referenceId))
      && (!issuedAfter || new Date(movement.createdAt || 0).getTime() >= new Date(issuedAfter).getTime())
    )
    .map((movement) => ({
      movement,
      material: materialById.get(String(movement.referenceId))!,
    }))
    .sort((a, b) => new Date(b.movement.createdAt || 0).getTime() - new Date(a.movement.createdAt || 0).getTime());
};

interface MovementTaskOption {
  id: string;
  label: string;
  crews: Array<{ id: string; name: string }>;
}

type PurchaseOrderItem = NonNullable<PurchaseOrder['items']>[number];

interface IssuedPickingMaterialRow {
  movement: StoresWorkspace['inventoryMovements'][number];
  material: TaskMaterial;
  task: RoleTaskSourceLink;
}

interface ReceivingMaterialRow {
  materialName: string;
  item: PurchaseOrderItem;
  po: PurchaseOrder;
}

const fmtDate = (value?: string | null) => {
  if (!value) return '-';
  const dateOnly = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
};

const dateOnlyTime = (value?: string | null) => {
  if (!value) return Number.NaN;
  const dateOnly = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  if (Number.isNaN(d.getTime())) return Number.NaN;
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const todayTime = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const isTodayDate = (value?: string | null) => dateOnlyTime(value) === todayTime();

const receivingAvailability = (expectedDate?: string | null) => {
  const expected = dateOnlyTime(expectedDate);
  if (Number.isNaN(expected)) return { canReceive: false, overdue: false, dueToday: false, missing: true };
  const today = todayTime();
  return { canReceive: expected <= today, overdue: expected < today, dueToday: expected === today, missing: false };
};

const receivingDateText = (expectedDate?: string | null) => {
  const availability = receivingAvailability(expectedDate);
  if (availability.missing) return 'Expected date missing';
  if (availability.dueToday) return 'Expected right now';
  if (availability.overdue) return `Overdue since ${fmtDate(expectedDate)}`;
  return `Next expected ${fmtDate(expectedDate)}`;
};

const movementDateLabel = (value?: string | null) => {
  if (!value) return 'No date recorded';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
};

const movementMonthLabel = (value?: string | null) => {
  if (!value) return 'No date recorded';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleDateString([], { month: 'long', year: 'numeric' });
};

const pickingDateKey = (value?: string | null) => {
  if (!value) return 'no-date';
  const dateOnly = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnly) return `${dateOnly[1]}-${dateOnly[2]}-${dateOnly[3]}`;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 'no-date' : d.toISOString().slice(0, 10);
};

const pickingDateLabel = (key: string) => {
  if (key === 'no-date') return 'No date recorded';
  return movementDateLabel(`${key}T00:00:00`);
};

const fmtCurrency = (value: number) =>
  new Intl.NumberFormat('en-JM', { style: 'currency', currency: 'JMD', maximumFractionDigits: 0 }).format(value);

const usedPercent = (row: InventorySummaryRow) => {
  const total = Number(row.totalQuantity || 0);
  return total > 0 ? Math.round((100 * Number(row.quantityInUse || 0)) / total) : 0;
};

const movementNeedsTask = (type: MovementType) => ['issue', 'return'].includes(type);
const movementNeedsReason = (type: MovementType) => ['adjustment', 'damage', 'theft', 'write_off'].includes(type);

const pickingSourceTaskId = (row: RoleTaskSourceLink) => row.sourceTaskId ?? row.sourceTask?.id ?? row.task.id;
const pickingSourceTask = (row: RoleTaskSourceLink) => row.sourceTask ?? row.task;
const pickingCrewOptions = (row: RoleTaskSourceLink) => {
  const crews = new Map<string, { id: string; name: string }>();
  (row.sourceTask?.teamAssignments ?? [])
    .map((assignment) => assignment.team)
    .filter((team): team is { id: string; name: string } => Boolean(team?.id && team?.name))
    .forEach((team) => crews.set(team.id, { id: team.id, name: team.name }));
  return [...crews.values()];
};
const pickingCrewLabel = (row: RoleTaskSourceLink) => {
  const crewNames = pickingCrewOptions(row).map((crew) => crew.name);
  return crewNames.length ? crewNames.join(', ') : 'Crew not assigned';
};

const isStoresPickingRoleTaskComplete = (row: RoleTaskSourceLink) =>
  ['inspection_passed', 'completed', 'done', 'closed'].includes(String(row.task.lifecycleStatus || '').toLowerCase());

const isClosedSourceTask = (row: RoleTaskSourceLink) =>
  ['inspection_passed', 'completed', 'done', 'closed', 'cancelled'].includes(String(row.sourceTask?.lifecycleStatus || '').toLowerCase());

const isPickingHandoff = (row: RoleTaskSourceLink) => {
  if (Boolean((row.sourceTask as any)?.pmIssuedToSupervisorId || (row.sourceTask as any)?.pmIssuedToSupervisor)) {
    return true;
  }
  const pmIssuedPicking = Boolean(row.metadata?.pmIssuedToSupervisorId || row.metadata?.pmIssuedById);
  if (pmIssuedPicking) {
    return false;
  }
  return row.triggerType === 'task_dispatched_stores';
};

const pickingRequestDate = (row: RoleTaskSourceLink) =>
  row.sourceTask?.pmIssuedAt
  || (row.metadata?.pmIssuedAt as string | undefined)
  || row.sourceTask?.plannedStartDate
  || row.sourceTask?.createdAt
  || row.task.plannedStartDate
  || row.task.createdAt
  || null;

const pickingCompletionDate = (
  row: RoleTaskSourceLink,
  materials: TaskMaterial[],
  movements: StoresWorkspace['inventoryMovements'] = []
) => {
  const issued = issuedPickingMovements(materials, movements, pickingRequestDate(row));
  return issued[0]?.movement.createdAt || row.task.actualEndDate || row.task.completedAt || row.sourceTask?.actualEndDate || row.sourceTask?.completedAt || row.task.createdAt || null;
};

const groupPickingRowsByDate = (
  rows: RoleTaskSourceLink[],
  dateFor: (row: RoleTaskSourceLink) => string | null | undefined
) => {
  const groups = new Map<string, RoleTaskSourceLink[]>();
  rows.forEach((row) => {
    const key = pickingDateKey(dateFor(row));
    groups.set(key, [...(groups.get(key) ?? []), row]);
  });
  return [...groups.entries()]
    .sort(([a], [b]) => {
      if (a === 'no-date') return 1;
      if (b === 'no-date') return -1;
      return b.localeCompare(a);
    })
    .map(([key, rowsForDate]) => ({
      key,
      label: pickingDateLabel(key),
      rows: rowsForDate,
    }));
};

function StoresMetric({ label, value, detail }: { label: string; value: number | string; detail: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: '#fff', minHeight: 104 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1 }}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{detail}</Typography>
    </Paper>
  );
}

function StoresMenuBar({ to, label, detail }: { to: string; label: string; detail: string }) {
  return (
    <Paper component={RouterLink} to={to} variant="outlined" sx={{ p: 2, borderRadius: 1, bgcolor: '#fff', color: 'inherit', textDecoration: 'none', display: 'block' }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 900 }}>{label}</Typography>
          <Typography variant="body2" color="text.secondary">{detail}</Typography>
        </Box>
        <Typography sx={{ color: 'text.secondary', fontWeight: 900 }}>Open</Typography>
      </Stack>
    </Paper>
  );
}

export default function StoresPage({ fieldMode = false, view = 'home' }: StoresPageProps) {
  const activeProjectId = useSelector((state: RootState) => state.projects.activeProjectId);
  const user = useSelector((state: RootState) => state.auth.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<LegacyTab>('tasks');
  const [data, setData] = useState<StoresWorkspace | null>(null);
  const [catalog, setCatalog] = useState<ProcurementCatalog | null>(null);
  const [inventory, setInventory] = useState<InventorySummaryRow[]>([]);
  const [teams, setTeams] = useState<TeamListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [movementOpen, setMovementOpen] = useState(false);
  const [savingMovement, setSavingMovement] = useState(false);
  const [inventoryView, setInventoryView] = useState<'category' | 'location'>('category');
  const [inventorySearch, setInventorySearch] = useState('');
  const [pickingMaterials, setPickingMaterials] = useState<Record<string, TaskMaterial[]>>({});
  const [pickingLoading, setPickingLoading] = useState(false);
  const [movement, setMovement] = useState({ ...EMPTY_MOVEMENT });
  const [lockedPickingIssue, setLockedPickingIssue] = useState<TaskMaterial | null>(null);
  const [routeDialog, setRouteDialog] = useState<{ materialName: string; guidance: YardZoneGuidance | null } | null>(null);
  const [openPickingGroups, setOpenPickingGroups] = useState<Set<string>>(() => new Set());
  const activeView: StoresFieldView | LegacyTab = fieldMode ? view : tab;
  const allowPersonnelSelection = ['admin', 'field_admin'].includes(user?.role ?? '');
  const selectedPersonnel = allowPersonnelSelection
    ? fieldPersonnelById(storesFieldPersonnel, searchParams.get(ACCOUNTS_PERSONNEL_QUERY_KEY))
    : null;
  const togglePickingGroup = (groupKey: string) => {
    setOpenPickingGroups((current) => {
      const next = new Set(current);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  const load = async () => {
    if (!activeProjectId) return;
    setLoading(true);
    setError(null);
    try {
      const [workspace, catalogRows, inventoryRows, teamRows] = await Promise.all([
        roleWorkspaceService.stores(activeProjectId),
        procurementService.getCatalog(),
        resourceService.getInventorySummary(),
        teamService.getTeams(),
      ]);
      setData(workspace);
      setCatalog(catalogRows);
      setInventory(inventoryRows);
      setTeams(teamRows);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to load stores workspace');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectId]);

  // Real picking lists: the resources linked to the construction task that Stores must issue against.
  useEffect(() => {
    const tasks = data?.tasks ?? [];
    if (!tasks.length) {
      setPickingMaterials({});
      return undefined;
    }
    let cancelled = false;
    setPickingLoading(true);
    Promise.all(tasks.map(async (row) => {
      const taskId = pickingSourceTaskId(row);
      return [taskId, await taskMaterialsService.listForTask(taskId)] as const;
    }))
      .then((entries) => {
        if (!cancelled) setPickingMaterials(Object.fromEntries(entries));
      })
      .catch((err: any) => {
        if (!cancelled) setError(err.response?.data?.error || err.message || 'Failed to load picking lists');
      })
      .finally(() => {
        if (!cancelled) setPickingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [data]);

  const completeTask = async (taskId: string) => {
    setCompletingId(taskId);
    try {
      await roleWorkspaceService.completeTask(taskId);
      await load();
    } finally {
      setCompletingId(null);
    }
  };

  const recordMovement = async () => {
    if (!activeProjectId) return;
    setSavingMovement(true);
    setError(null);
    try {
      const outbound = ['issue', 'damage', 'theft', 'write_off', 'transfer_out'].includes(movement.movementType);
      await inventoryMovementService.createMovement({
        projectId: activeProjectId,
        resourceId: movement.resourceId,
        storageLocationId: movement.storageLocationId,
        taskId: movement.taskId || undefined,
        movementType: movement.movementType,
        sourceType: movement.taskId ? 'task' : 'manual',
        quantity: outbound ? -Math.abs(Number(movement.quantity)) : Math.abs(Number(movement.quantity)),
        referenceType: lockedPickingIssue ? 'task_resource' : undefined,
        referenceId: lockedPickingIssue?.id,
        reasonCode: movement.reasonCode || undefined,
        notes: movement.notes || undefined,
        metadata: movement.crewId ? {
          crewId: movement.crewId,
          crewName: selectedCrewName,
        } : undefined,
      });
      setMovementOpen(false);
      setLockedPickingIssue(null);
      setMovement({ ...EMPTY_MOVEMENT });
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to record inventory movement');
    } finally {
      setSavingMovement(false);
    }
  };

  const openMovementDialog = () => {
    setLockedPickingIssue(null);
    setMovement({ ...EMPTY_MOVEMENT });
    setMovementOpen(true);
  };

  const inventoryByResource = useMemo(() => {
    const map = new Map<string, InventorySummaryRow[]>();
    inventory.forEach((row) => map.set(row.resourceId, [...(map.get(row.resourceId) ?? []), row]));
    return map;
  }, [inventory]);

  const movementTaskOptions = useMemo<MovementTaskOption[]>(() => {
    const options = new Map<string, MovementTaskOption>();
    (data?.tasks ?? []).forEach((row) => {
      const task = row.sourceTask;
      if (!task) return;
      const crews = (row.sourceTask?.teamAssignments ?? [])
        .map((assignment) => assignment.team)
        .filter((team): team is { id: string; name: string } => Boolean(team?.id && team?.name));
      const existing = options.get(task.id);
      const mergedCrews = new Map<string, { id: string; name: string }>();
      [...(existing?.crews ?? []), ...crews].forEach((crew) => mergedCrews.set(crew.id, crew));
      options.set(task.id, {
        id: task.id,
        label: `${task.taskCode} - ${task.name}`,
        crews: [...mergedCrews.values()].sort((a, b) => a.name.localeCompare(b.name)),
      });
    });
    return [...options.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);

  const selectedMovementTask = movementTaskOptions.find((row) => row.id === movement.taskId);
  const activeCrewOptions = useMemo(
    () => teams
      .filter((team) => team.isActive !== false)
      .sort((a, b) => a.name.localeCompare(b.name)),
    [teams]
  );
  const crewIdFromPickingMaterials = (materials: TaskMaterial[]) => {
    const normalize = (value?: string | null) => (value ?? '').trim().toLowerCase();
    const crewNames = new Set(activeCrewOptions.map((crew) => normalize(crew.name)));
    const labourCrew = materials.find((material) => {
      const resource = material.resource;
      return normalize(resource?.resourceType) === 'labour' && crewNames.has(normalize(resource?.name));
    });
    if (!labourCrew?.resource?.name) return '';
    return activeCrewOptions.find((crew) => normalize(crew.name) === normalize(labourCrew.resource?.name))?.id ?? '';
  };
  const selectedResource = catalog?.resources.find((row) => row.id === movement.resourceId) ?? null;
  const selectedResourceStockRows = inventoryByResource.get(movement.resourceId) ?? [];
  const selectedResourceAvailableStockRows = selectedResourceStockRows
    .filter((row) => Number(row.quantityAvailable || 0) > 0);
  const storageLocationOptions = lockedPickingIssue
    ? selectedResourceAvailableStockRows.map((row) => ({
      id: row.storageLocationId,
      label: `${row.storageLocationCode} · ${row.storageLocationName} (${row.quantityAvailable} available)`,
    }))
    : (catalog?.storageLocations ?? []).map((row) => ({
      id: row.id,
      label: `${row.locationCode} · ${row.name}`,
    }));
  const selectedCrew = activeCrewOptions.find((crew) => crew.id === movement.crewId)
    ?? selectedMovementTask?.crews.find((crew) => crew.id === movement.crewId)
    ?? null;
  const selectedCrewName = selectedCrew?.name ?? '';

  /** Open the real movement dialog prefilled to issue a picking-list item to its task. */
  const openIssueForItem = (taskId: string, material: TaskMaterial, crewId?: string, issuedAfter?: string | null) => {
    const taskOption = movementTaskOptions.find((row) => row.id === taskId);
    const stockRows = inventoryByResource.get(material.resourceId) ?? [];
    const stock = stockRows.find((row) => row.quantityAvailable > 0) ?? stockRows[0];
    const guidance = yardZoneGuidanceForResource({
      name: material.resource?.name,
      resourceCode: material.resource?.resourceCode,
      resourceType: material.resource?.resourceType,
      category: material.resource?.category,
    });
    setMovement({
      ...EMPTY_MOVEMENT,
      movementType: 'issue',
      resourceId: material.resourceId,
      storageLocationId: stock?.storageLocationId ?? '',
      taskId,
      crewId: crewId || taskOption?.crews[0]?.id || '',
      quantity: remainingPickingQuantity(material, data?.inventoryMovements, issuedAfter) || Math.max(1, Number(material.quantityRequired) || 1),
      notes: guidance ? `Yard routing: send to ${yardZoneLabel(guidance)}.` : '',
    });
    setLockedPickingIssue(material);
    setMovementOpen(true);
  };

  const openRouteForItem = (material: TaskMaterial) => {
    setRouteDialog({
      materialName: material.resource?.name ?? material.resourceId,
      guidance: yardZoneGuidanceForResource({
        name: material.resource?.name,
        resourceCode: material.resource?.resourceCode,
        resourceType: material.resource?.resourceType,
        category: material.resource?.category,
      }),
    });
  };

  const receivingPos = useMemo(
    () => (data?.purchaseOrders ?? []).filter((po) => !['cancelled', 'closed'].includes(po.status)),
    [data]
  );
  const pendingGrns = useMemo(
    () => (data?.goodsReceipts ?? []).filter((grn) => ['draft', 'pending_acceptance', 'disputed'].includes(grn.status)),
    [data]
  );
  const lowStock = inventory.filter((row) => row.lowStock || row.belowSafetyStock);
  const todayIso = new Date().toISOString().slice(0, 10);
  const pickingTasks = (data?.tasks ?? []).filter((row) => row.sourceType === 'task' && row.sourceTask && isPickingHandoff(row));
  const completedPickingTasks = pickingLoading
    ? []
    : pickingTasks.filter((row) => {
      const taskMaterials = pickingMaterials[pickingSourceTaskId(row)] ?? [];
      if (isStoresPickingRoleTaskComplete(row) || isClosedSourceTask(row)) return true;
      return taskMaterials.length > 0 && taskMaterials.every((material) => remainingPickingQuantity(material, data?.inventoryMovements, pickingRequestDate(row)) <= 0);
    });
  const activePickingTasks = pickingLoading
    ? pickingTasks
    : pickingTasks.filter((row) => {
      const taskMaterials = pickingMaterials[pickingSourceTaskId(row)] ?? [];
      return !completedPickingTasks.some((completed) => completed.task.id === row.task.id)
        && taskMaterials.some((material) => remainingPickingQuantity(material, data?.inventoryMovements, pickingRequestDate(row)) > 0);
    }).sort((a, b) =>
      pickingDateKey(pickingRequestDate(b)).localeCompare(pickingDateKey(pickingRequestDate(a)))
      || String(pickingSourceTask(a).taskCode ?? '').localeCompare(String(pickingSourceTask(b).taskCode ?? ''))
    );
  const completedPickingDateGroups = groupPickingRowsByDate(
    completedPickingTasks,
    (row) => pickingCompletionDate(row, pickingMaterials[pickingSourceTaskId(row)] ?? [], data?.inventoryMovements)
  );
  const issuedPickingMaterialDateGroups = useMemo(() => {
    const rows: IssuedPickingMaterialRow[] = [];
    const seenMovementIds = new Set<string>();
    pickingTasks.forEach((row) => {
      const taskMaterials = pickingMaterials[pickingSourceTaskId(row)] ?? [];
      issuedPickingMovements(taskMaterials, data?.inventoryMovements, pickingRequestDate(row)).forEach((issued) => {
        if (pickingDateKey(issued.movement.createdAt) !== todayIso) return;
        if (seenMovementIds.has(issued.movement.id)) return;
        seenMovementIds.add(issued.movement.id);
        rows.push({ ...issued, task: row });
      });
    });

    const groups = new Map<string, IssuedPickingMaterialRow[]>();
    rows
      .sort((a, b) => new Date(b.movement.createdAt || 0).getTime() - new Date(a.movement.createdAt || 0).getTime())
      .forEach((row) => {
        const key = pickingDateKey(row.movement.createdAt);
        groups.set(key, [...(groups.get(key) ?? []), row]);
      });

    return [...groups.entries()]
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([key, rowsForDate]) => ({
        key,
        label: pickingDateLabel(key),
        rows: rowsForDate,
      }));
  }, [data?.inventoryMovements, pickingMaterials, pickingTasks, todayIso]);
  const completedPickingToday = completedPickingTasks.filter((row) =>
    pickingDateKey(pickingCompletionDate(row, pickingMaterials[pickingSourceTaskId(row)] ?? [], data?.inventoryMovements)) === todayIso
  ).length;
  const pickingProgressPercent = pickingTasks.length > 0
    ? Math.round((completedPickingToday / pickingTasks.length) * 100)
    : 0;
  const filteredInventory = inventory.filter((row) => {
    const query = inventorySearch.trim().toLowerCase();
    if (!query) return true;
    return [
      row.resourceCode,
      row.resourceName,
      row.category,
      row.storageLocationCode,
      row.storageLocationName,
    ].some((value) => String(value ?? '').toLowerCase().includes(query));
  });
  const groupedInventory = filteredInventory.reduce<Record<string, InventorySummaryRow[]>>((groups, row) => {
    const key = inventoryView === 'category'
      ? (row.category || row.resourceType || 'Uncategorized')
      : (`${row.storageLocationCode} - ${row.storageLocationName}`);
    groups[key] = [...(groups[key] ?? []), row];
    return groups;
  }, {});
  const movementHistoryByMonth = useMemo(() => {
    const monthGroups = new Map<string, Map<string, NonNullable<StoresWorkspace['inventoryMovements']>>>();
    [...(data?.inventoryMovements ?? [])]
      .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
      .forEach((row) => {
        const monthLabel = movementMonthLabel(row.createdAt);
        const dateLabel = movementDateLabel(row.createdAt);
        if (!monthGroups.has(monthLabel)) monthGroups.set(monthLabel, new Map());
        const dayGroups = monthGroups.get(monthLabel)!;
        dayGroups.set(dateLabel, [...(dayGroups.get(dateLabel) ?? []), row]);
      });
    return [...monthGroups.entries()].map(([monthLabel, dayGroups]) => ({
      monthLabel,
      rows: [...dayGroups.values()].flat(),
      days: [...dayGroups.entries()],
    }));
  }, [data?.inventoryMovements]);
  const validMovement = Boolean(
    movement.resourceId &&
    movement.storageLocationId &&
    Number(movement.quantity) > 0 &&
    (!movementNeedsTask(movement.movementType) || movement.taskId) &&
    (!movementNeedsTask(movement.movementType) || movement.crewId) &&
    (!movementNeedsReason(movement.movementType) || movement.reasonCode.trim())
  );
  const grnsForPo = (poId: string) =>
    [...(data?.goodsReceipts ?? [])]
      .filter((grn) => grn.purchaseOrderId === poId)
      .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());

  const receiptStatusLabel = (poId: string) => {
    const grns = grnsForPo(poId);
    if (!grns.length) return 'No delivery note recorded yet';
    const latest = grns[0];
    return `Last delivery note ${latest.grnNumber} · ${latest.status.replace(/_/g, ' ')}`;
  };

  const deliveryReceivedToday = (poId: string) =>
    grnsForPo(poId).some((grn) => grn.status !== 'rejected' && isTodayDate(grn.receivedAt || grn.createdAt));

  const receivingMaterialsByName = useMemo(() => {
    const groups = new Map<string, ReceivingMaterialRow[]>();
    receivingPos.forEach((po) => {
      (po.items ?? []).forEach((item) => {
        const materialName = item.description || item.resource?.name || item.resourceId;
        groups.set(materialName, [...(groups.get(materialName) ?? []), { materialName, item, po }]);
      });
    });
    return [...groups.entries()]
      .map(([materialName, materialRows]) => [
        materialName,
        materialRows.sort((a, b) => new Date(a.po.expectedDeliveryDate || 0).getTime() - new Date(b.po.expectedDeliveryDate || 0).getTime()),
      ] as const)
      .sort(([a], [b]) => a.localeCompare(b));
  }, [receivingPos]);

  const receivingGuidanceForRow = (row: ReceivingMaterialRow) => {
    const resource = row.item.resource;
    const catalogResource = (catalog?.resources ?? []).find((resourceRow) =>
      resourceRow.id === row.item.resourceId || resourceRow.resourceCode === resource?.resourceCode
    );
    return yardZoneGuidanceForResource({
      name: catalogResource?.name || resource?.name || row.materialName,
      resourceCode: catalogResource?.resourceCode || resource?.resourceCode,
      resourceType: catalogResource?.resourceType,
      category: catalogResource?.category,
    });
  };

  const openRouteForReceivingRow = (row: ReceivingMaterialRow) => {
    setRouteDialog({
      materialName: row.materialName,
      guidance: receivingGuidanceForRow(row),
    });
  };

  const receivingTable = (rows: PurchaseOrder[]) => (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead><TableRow><TableCell>PO</TableCell><TableCell>Supplier</TableCell><TableCell>Status</TableCell><TableCell>Expected</TableCell><TableCell align="right">Value</TableCell><TableCell align="right">Action</TableCell></TableRow></TableHead>
        <TableBody>
          {rows.map((po) => (
            <TableRow key={po.id} hover>
              <TableCell><Typography fontWeight={800}>{po.poNumber}</Typography></TableCell>
              <TableCell>{po.supplier?.name || po.supplierId}</TableCell>
              <TableCell><Chip size="small" color={po.status === 'received' ? 'success' : po.status === 'partial_received' ? 'warning' : 'default'} label={po.status.replace(/_/g, ' ')} /></TableCell>
              <TableCell>{fmtDate(po.expectedDeliveryDate)}</TableCell>
              <TableCell align="right">{fmtCurrency(Number(po.totalAmount || 0))}</TableCell>
              <TableCell align="right">
                <Button component={RouterLink} to={`/field/purchase-orders/${po.id}`}>{po.status === 'received' ? 'Review' : 'Receive'}</Button>
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={6}><Alert severity="info">No confirmed deliveries are waiting.</Alert></TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );

  const openSelectedPersonnel = (personnelId: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('view', 'stores');
    nextParams.set(ACCOUNTS_PERSONNEL_QUERY_KEY, personnelId);
    setSearchParams(nextParams);
  };

  const clearSelectedPersonnel = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('view', 'stores');
    nextParams.delete(ACCOUNTS_PERSONNEL_QUERY_KEY);
    setSearchParams(nextParams);
  };

  const personnelIndicatorGrid = (
    <Stack spacing={1.5}>
      <Typography variant="h4" fontWeight={900}>Stores Personnel</Typography>
      <Grid container spacing={1.5}>
        {storesFieldPersonnel.map((person) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={person.id}>
            <PersonnelFolderCard
              name={person.name}
              email={person.email}
              credentialHint={person.credentialHint}
              onClick={() => openSelectedPersonnel(person.id)}
            />
          </Grid>
        ))}
      </Grid>
    </Stack>
  );

  const personnelContextCard = selectedPersonnel ? (
    <Paper sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={900} textTransform="uppercase">Stores personnel</Typography>
          <Typography variant="h5" fontWeight={900}>{selectedPersonnel.name}</Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
            <Chip size="small" label={`Login: ${selectedPersonnel.email}`} />
            <Chip size="small" label={selectedPersonnel.credentialHint} />
            <Chip size="small" label={selectedPersonnel.team} />
          </Stack>
        </Box>
        <Button size="small" variant="outlined" onClick={clearSelectedPersonnel} sx={{ borderRadius: 1, fontWeight: 900 }}>
          Back to Stores Personnel
        </Button>
      </Stack>
    </Paper>
  ) : null;

  if (fieldMode && activeView === 'home' && allowPersonnelSelection && !selectedPersonnel) {
    return (
      <Stack spacing={2}>
        {!activeProjectId && <Alert severity="warning">Select an active project to load stores data.</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
        {personnelIndicatorGrid}
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {!fieldMode && (
        <PageHeader
          title="Stores"
          subtitle="Picking, put-away, and inventory handoff queues for scheduled work and accepted deliveries."
        />
      )}
      {!activeProjectId && <Alert severity="warning">Select an active project to load stores data.</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
      {fieldMode && personnelContextCard}

      {data && activeView === 'home' && (
        <Stack spacing={2}>
          <Typography variant="h4" sx={{ fontSize: 30, fontWeight: 900, letterSpacing: 0 }}>Stores</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1 }}>
            <StoresMetric label="Deliveries in" value={receivingPos.length} detail="Confirmed orders awaiting receipt" />
            <StoresMetric label="Receipts pending" value={pendingGrns.length} detail="Delivery notes awaiting acceptance" />
            <StoresMetric label="Stock alerts" value={lowStock.length} detail="Low stock or below safety stock" />
            <StoresMetric label="Picking to deliver" value={activePickingTasks.length} detail="Picking list tasks awaiting delivery" />
          </Box>

          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: '#fff' }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900, textTransform: 'uppercase' }}>
                Today's picking-list completion
              </Typography>
              <Typography variant="caption" fontWeight={900}>
                {pickingProgressPercent}%
              </Typography>
            </Stack>
            <LinearProgress variant="determinate" value={pickingProgressPercent} sx={{ height: 8, borderRadius: 1, mt: 0.75 }} />
            <Typography variant="caption" color="text.secondary">
              {completedPickingToday} of {pickingTasks.length} picking-list tasks completed today
            </Typography>
          </Paper>

          <StoresMenuBar
            to="/field/stores/receiving"
            label="Shipment receiving task"
            detail={receivingPos[0] ? `${receivingPos[0].poNumber} - receive against purchase order` : 'No confirmed deliveries waiting'}
          />
          <StoresMenuBar to="/field/stores/stock" label="Inventory monitoring task" detail="Inventory check by category or storage location" />
          <StoresMenuBar to="/field/stores/picking" label="Task picking list" detail="Materials, tools, equipment and supplies to deliver" />
        </Stack>
      )}

      {data && activeView === 'receiving' && (
        <Stack spacing={2}>
          <Typography variant="h4" sx={{ fontSize: 30, fontWeight: 900, letterSpacing: 0 }}>Stores</Typography>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>Shipment receiving</Typography>
          {receivingMaterialsByName.map(([materialName, rows]) => {
            const orderedTotal = rows.reduce((sum, row) => sum + Number(row.item.quantityOrdered || 0), 0);
            const receivedTotal = rows.reduce((sum, row) => sum + Number(row.item.quantityReceived || 0), 0);
            const unit = rows.find((row) => row.item.resource?.unitOfMeasure)?.item.resource?.unitOfMeasure ?? '';
            const nextExpected = rows
              .map((row) => row.item.expectedDeliveryDate || row.po.expectedDeliveryDate)
              .filter(Boolean)
              .sort((a, b) => new Date(a || 0).getTime() - new Date(b || 0).getTime())[0];
            const nextAvailability = receivingAvailability(nextExpected);
            const hasReadyOrder = rows.some((row) =>
              ['confirmed', 'partial_received', 'received'].includes(row.po.status)
              && receivingAvailability(row.item.expectedDeliveryDate || row.po.expectedDeliveryDate).canReceive
            );
            const receivedTodayCount = new Set(rows
              .filter((row) => deliveryReceivedToday(row.po.id))
              .map((row) => row.po.id)).size;
            return (
              <Accordion
                key={materialName}
                variant="outlined"
                disableGutters
                sx={{
                  borderRadius: 1,
                  bgcolor: receivedTodayCount > 0 || (nextAvailability.dueToday && hasReadyOrder) ? '#e8f5e9' : '#fff',
                  borderColor: receivedTodayCount > 0 || (nextAvailability.dueToday && hasReadyOrder) ? 'success.main' : 'divider',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ width: '100%', pr: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 900 }}>{materialName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {receivingDateText(nextExpected)} | {rows.length} order line{rows.length === 1 ? '' : 's'}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {receivedTodayCount > 0 && <Chip size="small" color="success" label={`Delivery received today (${receivedTodayCount})`} />}
                      {nextAvailability.dueToday && hasReadyOrder && <Chip size="small" color="success" label="Ready to receive" />}
                      <Chip size="small" label={`Ordered ${orderedTotal} ${unit}`.trim()} />
                      <Chip size="small" color={receivedTotal > 0 ? 'success' : 'default'} label={`Received ${receivedTotal} ${unit}`.trim()} />
                    </Stack>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 760, '& td, & th': { py: 0.65, px: 0.75, fontSize: 12 } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>PO</TableCell>
                          <TableCell>Supplier</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Expected</TableCell>
                          <TableCell align="right">Ordered</TableCell>
                          <TableCell align="right">Received</TableCell>
                          <TableCell align="right">Rejected</TableCell>
                          <TableCell>Delivery note</TableCell>
                          <TableCell align="right">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map(({ item, po }) => {
                          const expectedDate = item.expectedDeliveryDate || po.expectedDeliveryDate;
                          const availability = receivingAvailability(expectedDate);
                          const fullyReceived = po.status === 'received';
                          const readyStatus = ['confirmed', 'partial_received'].includes(po.status);
                          const receivedToday = deliveryReceivedToday(po.id);
                          return (
                            <TableRow key={`${po.id}-${item.id}`} hover>
                              <TableCell>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => openRouteForReceivingRow({ materialName, item, po })}
                                  sx={{ justifyContent: 'flex-start', p: 0, minWidth: 0, textAlign: 'left', fontWeight: 900, textTransform: 'none' }}
                                >
                                  {po.poNumber}
                                </Button>
                                <Typography variant="caption" color="text.secondary" display="block">Storage map</Typography>
                              </TableCell>
                              <TableCell>{po.supplier?.name || po.supplierId}</TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                  <Chip
                                    size="small"
                                    color={fullyReceived ? 'success' : po.status === 'partial_received' ? 'warning' : 'default'}
                                    label={po.status.replace(/_/g, ' ')}
                                  />
                                  {receivedToday && <Chip size="small" color="success" label="Delivery received" />}
                                  {!fullyReceived && availability.overdue && <Chip size="small" color="error" label="overdue" />}
                                  {!fullyReceived && availability.dueToday && readyStatus && <Chip size="small" color="success" label="expected right now" />}
                                  {!fullyReceived && !readyStatus && <Chip size="small" color="warning" label="awaiting confirmation" />}
                                  {!fullyReceived && availability.missing && <Chip size="small" color="warning" label="expected date missing" />}
                                  {!fullyReceived && !availability.canReceive && !availability.missing && <Chip size="small" label="upcoming" />}
                                </Stack>
                              </TableCell>
                              <TableCell>{availability.dueToday ? 'Expected right now' : fmtDate(expectedDate)}</TableCell>
                              <TableCell align="right">{Number(item.quantityOrdered || 0)} {item.resource?.unitOfMeasure ?? ''}</TableCell>
                              <TableCell align="right">{Number(item.quantityReceived || 0)} {item.resource?.unitOfMeasure ?? ''}</TableCell>
                              <TableCell align="right">{Number(item.quantityRejected || 0) || '-'}</TableCell>
                              <TableCell>{receiptStatusLabel(po.id)}</TableCell>
                              <TableCell align="right">
                                {fullyReceived || (readyStatus && availability.canReceive) ? (
                                  <Button size="small" variant="contained" component={RouterLink} to={`/field/purchase-orders/${po.id}`}>
                                    {fullyReceived ? 'Review' : 'Receive'}
                                  </Button>
                                ) : (
                                  <Typography variant="caption" color={availability.missing ? 'warning.main' : 'text.secondary'} fontWeight={800}>
                                    {!readyStatus ? 'Awaiting procurement confirmation' : availability.missing ? 'Expected date missing' : `Available on ${fmtDate(expectedDate)}`}
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
          {receivingPos.length === 0 && <Alert severity="info">No confirmed deliveries are waiting to be received.</Alert>}
        </Stack>
      )}

      {data && activeView === 'picking' && (
        <Stack spacing={2}>
          <Typography variant="h4" sx={{ fontSize: 30, fontWeight: 900, letterSpacing: 0 }}>Stores</Typography>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>Task Picking List</Typography>
          {pickingLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={28} /></Box>}
          {activePickingTasks.map((row, taskIndex) => {
            const source = pickingSourceTask(row);
            const sourceTaskId = pickingSourceTaskId(row);
            const sourceCrews = pickingCrewOptions(row);
            const taskMaterials = pickingMaterials[sourceTaskId] ?? [];
            const pickingCrewId = sourceCrews[0]?.id ?? crewIdFromPickingMaterials(taskMaterials);
            const requestDate = pickingRequestDate(row);
            const materials = taskMaterials
              .filter((material) => remainingPickingQuantity(material, data.inventoryMovements, requestDate) > 0);
            const previousPicking = issuedPickingMovements(taskMaterials, data.inventoryMovements, requestDate);
            const groupKey = pickingDateKey(pickingRequestDate(row));
            const previousGroupKey = taskIndex > 0 ? pickingDateKey(pickingRequestDate(activePickingTasks[taskIndex - 1])) : '';
            const showDateHeader = taskIndex === 0 || groupKey !== previousGroupKey;
            const groupClosed = !openPickingGroups.has(groupKey);
            if (!showDateHeader && groupClosed) return null;
            return (
              <Box key={row.task.id}>
                {showDateHeader && (
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => togglePickingGroup(groupKey)}
                    endIcon={<ExpandMoreIcon sx={{ transform: groupClosed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 150ms ease' }} />}
                    sx={{
                      justifyContent: 'space-between',
                      mb: 1,
                      py: 1.1,
                      px: 1.5,
                      borderColor: 'text.primary',
                      color: 'text.primary',
                      textAlign: 'left',
                      textTransform: 'uppercase',
                      fontSize: 17,
                      fontWeight: 950,
                      letterSpacing: 0,
                    }}
                  >
                    Issued for task picking - {pickingDateLabel(groupKey)}
                  </Button>
                )}
              {!groupClosed && <Accordion disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 900 }}>{source.taskCode} - {source.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{pickingCrewLabel(row)}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {materials.length > 0 ? (
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead><TableRow><TableCell>Item specification</TableCell><TableCell align="right">Quantity required</TableCell><TableCell>Store location</TableCell><TableCell>Headed to</TableCell><TableCell align="right">Action</TableCell></TableRow></TableHead>
                        <TableBody>
                          {materials.map((material) => {
                            const stock = inventoryByResource.get(material.resourceId)?.[0];
                            const guidance = yardZoneGuidanceForResource({
                              name: material.resource?.name,
                              resourceCode: material.resource?.resourceCode,
                              resourceType: material.resource?.resourceType,
                              category: material.resource?.category,
                            });
                            return (
                              <TableRow key={material.id}>
                                <TableCell>
                                  <Stack direction="row" spacing={1} alignItems="flex-start">
                                    <Box sx={{ width: 16, height: 16, mt: 0.25, border: '2px solid', borderColor: 'text.secondary', borderRadius: 0.5, flex: '0 0 auto' }} />
                                    <Box sx={{ minWidth: 0 }}>
                                      <Button
                                        variant="text"
                                        size="small"
                                        onClick={() => openRouteForItem(material)}
                                        sx={{ justifyContent: 'flex-start', p: 0, minWidth: 0, textAlign: 'left', fontWeight: 900, textTransform: 'none' }}
                                      >
                                        {material.resource?.resourceCode || material.resourceId}
                                      </Button>
                                      <Typography variant="body2">{material.resource?.name}</Typography>
                                    </Box>
                                  </Stack>
                                </TableCell>
                                <TableCell align="right">
                                  {remainingPickingQuantity(material, data.inventoryMovements, requestDate)} {material.resource?.unitOfMeasure || ''}
                                </TableCell>
                                <TableCell>{stock ? `${stock.storageLocationCode} - ${stock.storageLocationName}` : 'Not in stores'}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={800}>{yardZoneLabel(guidance)}</Typography>
                                  {guidance && <Typography variant="caption" color="text.secondary">Based on {guidance.reason}</Typography>}
                                </TableCell>
                                <TableCell align="right">
                                  <Button
                                    size="small"
                                    variant="contained"
                                    disabled={availableStockQuantity(inventoryByResource.get(material.resourceId)) <= 0}
                                    onClick={() => openIssueForItem(sourceTaskId, material, pickingCrewId, requestDate)}
                                  >
                                    {availableStockQuantity(inventoryByResource.get(material.resourceId)) <= 0 ? 'No stock' : 'Issue'}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                  ) : (
                    <Alert severity="info">
                      {pickingLoading ? 'Loading picking list…' : 'No resources are linked to this task yet.'}
                    </Alert>
                  )}
                  {previousPicking.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontWeight: 900, mb: 1 }}>Previous picking list requests</Typography>
                      <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Item issued</TableCell>
                              <TableCell align="right">Quantity issued</TableCell>
                              <TableCell>Issued date</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {previousPicking.map(({ movement, material }) => (
                              <TableRow key={movement.id}>
                                <TableCell>
                                  <Typography fontWeight={800}>{material.resource?.resourceCode || material.resourceId}</Typography>
                                  <Typography variant="body2">{material.resource?.name}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  {Math.abs(Number(movement.quantity))} {material.resource?.unitOfMeasure || ''}
                                </TableCell>
                                <TableCell>{fmtDate(movement.createdAt)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>}
              </Box>
            );
          })}
          {!pickingLoading && activePickingTasks.length === 0 && <Alert severity="info">No picking list tasks are waiting for stores.</Alert>}
          <Box sx={{ pt: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>Completed materials today</Typography>
          </Box>
          {issuedPickingMaterialDateGroups.map((group) => (
            <Accordion key={`issued-materials-${group.key}`} variant="outlined" disableGutters sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 1 }}>
                  <Typography fontWeight={900}>Issued - {group.label}</Typography>
                  <Chip size="small" label={`${group.rows.length} material${group.rows.length === 1 ? '' : 's'}`} />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Item issued</TableCell>
                        <TableCell>Task</TableCell>
                        <TableCell>Crew</TableCell>
                        <TableCell>Issued date</TableCell>
                        <TableCell align="right">Quantity issued</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.rows.map(({ movement, material, task }) => {
                        const metadata = movement.metadata ?? {};
                        const crewName = typeof metadata.crewName === 'string' ? metadata.crewName : pickingCrewLabel(task);
                        const source = pickingSourceTask(task);
                        return (
                          <TableRow key={movement.id}>
                            <TableCell>
                              <Typography fontWeight={800}>{material.resource?.resourceCode || material.resourceId}</Typography>
                              <Typography variant="body2">{material.resource?.name}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight={800}>{source.taskCode}</Typography>
                              <Typography variant="body2">{source.name}</Typography>
                            </TableCell>
                            <TableCell>{crewName || '-'}</TableCell>
                            <TableCell>{fmtDate(movement.createdAt)}</TableCell>
                            <TableCell align="right">
                              {Math.abs(Number(movement.quantity))} {material.resource?.unitOfMeasure || ''}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
          {!pickingLoading && issuedPickingMaterialDateGroups.length === 0 && <Alert severity="info">No materials have been issued today yet.</Alert>}
          <Box sx={{ pt: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>Completed</Typography>
          </Box>
          {completedPickingDateGroups.map((group) => (
            <Box key={`completed-${group.key}`}>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>
                Completed - {group.label}
              </Typography>
              <Stack spacing={1}>
                {group.rows.map((row) => {
                  const source = pickingSourceTask(row);
                  const sourceTaskId = pickingSourceTaskId(row);
                  const taskMaterials = pickingMaterials[sourceTaskId] ?? [];
                  const previousPicking = issuedPickingMovements(taskMaterials, data.inventoryMovements, pickingRequestDate(row));
                  const completedMaterials = previousPicking.length
                    ? previousPicking
                    : taskMaterials.map((material) => ({ movement: null, material }));
                  return (
                    <Accordion key={`completed-${row.task.id}`} disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 900 }}>{source.taskCode} - {source.name}</Typography>
                          <Typography variant="body2" color="text.secondary">{pickingCrewLabel(row)}</Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ overflowX: 'auto' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Item issued</TableCell>
                                <TableCell align="right">Quantity issued</TableCell>
                                <TableCell>Completed date</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {completedMaterials.map(({ movement, material }) => (
                                <TableRow key={movement?.id ?? material.id}>
                                  <TableCell>
                                    <Typography fontWeight={800}>{material.resource?.resourceCode || material.resourceId}</Typography>
                                    <Typography variant="body2">{material.resource?.name}</Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    {movement ? Math.abs(Number(movement.quantity)) : Number(material.quantityRequired || 0)} {material.resource?.unitOfMeasure || ''}
                                  </TableCell>
                                  <TableCell>{fmtDate(movement?.createdAt || pickingCompletionDate(row, taskMaterials, data.inventoryMovements))}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Stack>
            </Box>
          ))}
          {!pickingLoading && completedPickingTasks.length === 0 && <Alert severity="info">No completed picking list tasks yet.</Alert>}
        </Stack>
      )}

      {data && activeView === 'stock' && (
        <Stack spacing={2}>
          <Typography variant="h4" sx={{ fontSize: 30, fontWeight: 900, letterSpacing: 0 }}>Stores</Typography>
          {lowStock.length > 0 && (
            <Alert severity={lowStock.some((row) => row.belowSafetyStock) ? 'error' : 'warning'}>
              {lowStock.length} material{lowStock.length === 1 ? '' : 's'} need recouping. Search or filter the list below before issuing more stock.
            </Alert>
          )}
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 900 }}>Inventory Monitoring</Typography>
            <Button variant={inventoryView === 'category' ? 'contained' : 'outlined'} onClick={() => setInventoryView('category')}>By category</Button>
            <Button variant={inventoryView === 'location' ? 'contained' : 'outlined'} onClick={() => setInventoryView('location')}>Location</Button>
          </Stack>
          <TextField
            fullWidth
            size="small"
            label="Search materials"
            value={inventorySearch}
            onChange={(event) => setInventorySearch(event.target.value)}
          />
          <Stack direction="row" justifyContent="flex-end">
            <Button variant="contained" startIcon={<AddIcon />} onClick={openMovementDialog}>Record movement</Button>
          </Stack>

          {Object.entries(groupedInventory).map(([group, rows]) => (
            <Accordion key={group} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 900 }}>{group}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Item</TableCell><TableCell align="right">Available</TableCell><TableCell align="right">Reserved</TableCell><TableCell align="right">In use</TableCell><TableCell align="right">Used</TableCell><TableCell>Status</TableCell><TableCell>Storage location</TableCell><TableCell>Yard zone</TableCell></TableRow></TableHead>
                    <TableBody>
                      {rows.map((row) => {
                        const guidance = yardZoneGuidanceForResource({
                          name: row.resourceName,
                          resourceCode: row.resourceCode,
                          resourceType: row.resourceType,
                          category: row.category,
                        });
                        return (
                          <TableRow key={row.inventoryId}>
                            <TableCell><Typography fontWeight={800}>{row.resourceCode}</Typography>{row.resourceName}</TableCell>
                            <TableCell align="right">{row.quantityAvailable} {row.unitOfMeasure}</TableCell>
                            <TableCell align="right">{row.quantityReserved}</TableCell>
                            <TableCell align="right">{row.quantityInUse}</TableCell>
                            <TableCell align="right">
                              <Stack spacing={0.5} alignItems="flex-end">
                                <Typography variant="caption" fontWeight={900}>{usedPercent(row)}%</Typography>
                                <LinearProgress variant="determinate" value={usedPercent(row)} sx={{ width: 80, height: 6, borderRadius: 1 }} />
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                color={row.belowSafetyStock ? 'error' : row.lowStock ? 'warning' : 'success'}
                                label={row.belowSafetyStock ? 'Below safety' : row.lowStock ? 'Low stock' : 'OK'}
                              />
                            </TableCell>
                            <TableCell>{row.storageLocationCode} - {row.storageLocationName}</TableCell>
                            <TableCell>{yardZoneLabel(guidance)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
          {inventory.length === 0 && <Alert severity="info">No inventory records on this project yet.</Alert>}
          {inventory.length > 0 && filteredInventory.length === 0 && <Alert severity="info">No materials match that search.</Alert>}

          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: '#fff' }}>
            <Typography sx={{ fontWeight: 900, mb: 1 }}>Movement history</Typography>
            {movementHistoryByMonth.length > 0 ? (
              <Stack spacing={1}>
                {movementHistoryByMonth.map((month) => (
                  <Accordion key={month.monthLabel} variant="outlined" disableGutters sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 1 }}>
                        <Typography fontWeight={900}>{month.monthLabel}</Typography>
                        <Chip size="small" label={`${month.rows.length} movement${month.rows.length === 1 ? '' : 's'}`} />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={1}>
                        {month.days.map(([dateLabel, rows]) => (
                          <Accordion key={`${month.monthLabel}-${dateLabel}`} variant="outlined" disableGutters sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 1 }}>
                                <Typography fontWeight={900}>{dateLabel}</Typography>
                                <Chip size="small" label={`${rows.length} item${rows.length === 1 ? '' : 's'}`} />
                              </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Box sx={{ overflowX: 'auto' }}>
                                <Table size="small">
                                  <TableHead><TableRow><TableCell>Movement</TableCell><TableCell>Resource</TableCell><TableCell>Task</TableCell><TableCell>Crew</TableCell><TableCell align="right">Quantity</TableCell><TableCell>Reason</TableCell></TableRow></TableHead>
                                  <TableBody>
                                    {rows.map((row) => {
                                      const metadata = row.metadata ?? {};
                                      const crewName = typeof metadata.crewName === 'string' ? metadata.crewName : '';
                                      return (
                                        <TableRow key={row.id}>
                                          <TableCell>{row.movementType.replace(/_/g, ' ')}</TableCell>
                                          <TableCell>{catalog?.resources.find((item) => item.id === row.resourceId)?.name || row.resourceId}</TableCell>
                                          <TableCell>
                                            {data.tasks.find((item) => item.task.id === row.taskId || item.sourceTask?.id === row.taskId)?.sourceTask?.taskCode
                                              || data.tasks.find((item) => item.task.id === row.taskId || item.sourceTask?.id === row.taskId)?.task.taskCode
                                              || '-'}
                                          </TableCell>
                                          <TableCell>{crewName || '-'}</TableCell>
                                          <TableCell align="right">{row.quantity}</TableCell>
                                          <TableCell>{row.reasonCode || row.notes || '-'}</TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            ) : (
              <Alert severity="info">No inventory movements recorded on this project yet.</Alert>
            )}
          </Paper>
        </Stack>
      )}

      {data && !fieldMode && (
        <Paper sx={{ borderRadius: 1 }}>
          <Tabs value={tab} onChange={(_, value: LegacyTab) => setTab(value)} sx={{ borderBottom: 1, borderColor: 'divider' }}><Tab value="tasks" label={`Role Tasks (${data.tasks.length})`} /><Tab value="put-away" label={`Put-away (${data.goodsReceipts.length})`} /><Tab value="movements" label="Movements" /></Tabs>
          <Box sx={{ p: 2 }}>{activeView === 'tasks' && <RoleTaskQueue rows={data.tasks} onComplete={completeTask} completingId={completingId} />}{activeView === 'put-away' && receivingTable(receivingPos)}{activeView === 'movements' && <Alert severity="info">Use Stores Field Mode for project-scoped inventory movements.</Alert>}</Box>
        </Paper>
      )}

      <Dialog open={Boolean(routeDialog)} onClose={() => setRouteDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Storage route map</DialogTitle>
        <DialogContent>
          <YardRouteMap
            guidance={routeDialog?.guidance ?? null}
            materialName={routeDialog?.materialName}
            compact
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRouteDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={movementOpen} onClose={() => {
        setMovementOpen(false);
        setLockedPickingIssue(null);
      }} maxWidth="sm" fullWidth>
        <DialogTitle>Record inventory movement</DialogTitle>
        <DialogContent><Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth><InputLabel>Movement</InputLabel><Select disabled={Boolean(lockedPickingIssue)} value={movement.movementType} label="Movement" onChange={(event) => setMovement({ ...movement, movementType: event.target.value as MovementType, taskId: '', crewId: '', reasonCode: '' })}><MenuItem value="issue">Issue to task</MenuItem><MenuItem value="return">Return from task</MenuItem><MenuItem value="damage">Damage</MenuItem><MenuItem value="theft">Theft</MenuItem><MenuItem value="write_off">Write-off</MenuItem></Select></FormControl>
          <Autocomplete
            disabled={Boolean(lockedPickingIssue)}
            options={catalog?.resources ?? []}
            value={selectedResource}
            onChange={(_, resource) => setMovement({ ...movement, resourceId: resource?.id ?? '' })}
            getOptionLabel={(resource) => `${resource.resourceCode || 'Resource'} - ${resource.name}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Resource" placeholder="Search material, tool, or code" />}
          />
          <FormControl fullWidth>
            <InputLabel>Storage location</InputLabel>
            <Select
              value={movement.storageLocationId}
              label="Storage location"
              onChange={(event) => setMovement({ ...movement, storageLocationId: event.target.value })}
            >
              {storageLocationOptions.map((row) => (
                <MenuItem key={row.id} value={row.id}>
                  {row.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {movementNeedsTask(movement.movementType) && (
            <FormControl fullWidth>
              <InputLabel>Task</InputLabel>
              <Select
                disabled={Boolean(lockedPickingIssue)}
                value={movement.taskId}
                label="Task"
                onChange={(event) => {
                  const taskId = event.target.value;
                  const option = movementTaskOptions.find((row) => row.id === taskId);
                  setMovement({ ...movement, taskId, crewId: option?.crews[0]?.id ?? '' });
                }}
              >
                {movementTaskOptions.map((row) => <MenuItem key={row.id} value={row.id}>{row.label}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {movementNeedsTask(movement.movementType) ? (
            <Autocomplete
              disabled={Boolean(lockedPickingIssue && selectedMovementTask?.crews.length)}
              options={activeCrewOptions}
              value={selectedCrew as TeamListItem | null}
              onChange={(_, crew) => setMovement({ ...movement, crewId: crew?.id ?? '' })}
              getOptionLabel={(crew) => crew.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => <TextField {...params} label="Crew receiving material" placeholder="Search crew" required />}
            />
          ) : null}
          {movement.resourceId && (
            <Alert severity="info">
              Yard routing: {yardZoneLabel(yardZoneGuidanceForResource(catalog?.resources.find((row) => row.id === movement.resourceId) ?? {}))}
            </Alert>
          )}
          <TextField type="number" label="Quantity" inputProps={{ min: 0.01, step: 0.01 }} value={movement.quantity} onChange={(event) => setMovement({ ...movement, quantity: Number(event.target.value) })} />
          {movementNeedsReason(movement.movementType) && <TextField label="Reason code" required value={movement.reasonCode} onChange={(event) => setMovement({ ...movement, reasonCode: event.target.value })} helperText="Required for damage, theft, and write-off audit events." />}
          <TextField label="Notes" multiline minRows={2} value={movement.notes} onChange={(event) => setMovement({ ...movement, notes: event.target.value })} />
          {!navigator.onLine && <Alert severity="warning">Inventory writes require a connection in this localhost review build. Your selections remain on screen until the connection returns.</Alert>}
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => {
          setMovementOpen(false);
          setLockedPickingIssue(null);
        }}>Cancel</Button><Button variant="contained" disabled={!validMovement || savingMovement || !navigator.onLine} onClick={recordMovement}>Record</Button></DialogActions>
      </Dialog>
    </Stack>
  );
}
